import { IListing } from '@travelnest/typewriter';

import { idOrLegacyId } from '../../entities/properties/selectors';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../store/actions';
import { ThunkAction } from '../../store/middleware';

export const getByPropertyActions = createActionCreators<
	{ T: 'LISTINGS_BY_PROPERTY_ID_REQUEST', P: { id: string } },
	{ T: 'LISTINGS_BY_PROPERTY_ID_RECEIVE', P: { id: string, listings: IListing[] } },
	{ T: 'LISTINGS_BY_PROPERTY_ID_FAILURE', P: { id: string, error: Error } }
	>('LISTINGS_BY_PROPERTY_ID_REQUEST', 'LISTINGS_BY_PROPERTY_ID_RECEIVE', 'LISTINGS_BY_PROPERTY_ID_FAILURE');

const receiveListings = (listings: IListing[]): IListing[] => listings.map((l) => {
	return {
		...l,
		created_at: new Date(l.created_at),
		url_checked_at: l.url_checked_at ? new Date(l.url_checked_at) : l.url_checked_at
	};
});

export const getListingsByPropertyId = (id: string | number ): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getByPropertyActions.LISTINGS_BY_PROPERTY_ID_REQUEST({ id: propertyId }));
	try {
		const listings = receiveListings(await api.listings.getByPropertyId(propertyId));
		dispatch(getByPropertyActions.LISTINGS_BY_PROPERTY_ID_RECEIVE({ id: propertyId, listings }));
	} catch (error) {
		dispatch(getByPropertyActions.LISTINGS_BY_PROPERTY_ID_FAILURE({ id: propertyId, error }));
		throw error;
	}
};

export const getListingActions = createActionCreators<
	{ T: 'LISTING_BY_LISTING_ID_REQUEST' },
	{ T: 'LISTING_BY_LISTING_ID_RECEIVE', P: IListing },
	{ T: 'LISTING_BY_LISTING_ID_FAILURE', P: { id: string, error: Error } }
	>('LISTING_BY_LISTING_ID_REQUEST', 'LISTING_BY_LISTING_ID_RECEIVE', 'LISTING_BY_LISTING_ID_FAILURE');

export const getListingById = (id: string): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	dispatch(getListingActions.LISTING_BY_LISTING_ID_REQUEST());
	try {
		const listing = receiveListings([await api.listings.getByListingId(id)]);
		dispatch(getListingActions.LISTING_BY_LISTING_ID_RECEIVE(listing[0]));
	} catch (error) {
		dispatch(getListingActions.LISTING_BY_LISTING_ID_FAILURE({ id, error }));
		throw error;
	}
};

export const updateListingActions = createActionCreators<
	{ T: 'LISTING_UPDATE_REQUEST', P: { id: string } },
	{ T: 'LISTING_UPDATE_RECEIVE', P: IListing },
	{ T: 'LISTING_UPDATE_FAILURE', P: { id: string, error: Error } }
	>('LISTING_UPDATE_REQUEST', 'LISTING_UPDATE_RECEIVE', 'LISTING_UPDATE_FAILURE');

export const updateListing = (listing: IListing): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	dispatch(updateListingActions.LISTING_UPDATE_REQUEST({ id: listing.id }));
	try {
		const next = receiveListings([await api.listings.update(listing)]);
		dispatch(updateListingActions.LISTING_UPDATE_RECEIVE(next[0]));
	} catch (error) {
		dispatch(updateListingActions.LISTING_UPDATE_FAILURE({ id: listing.id, error }));
		throw error;
	}
};

const actions = {
	...getByPropertyActions,
	...getListingActions,
	...updateListingActions
};

type ListingActions = ActionValues<typeof actions>;
type ListingActionTypes = ActionTypeNames<ListingActions>;
export type Actions = ListingActions[keyof ListingActions];
export const ActionTypes = Object.keys(actions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as ListingActionTypes);
